<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="state.show"
        class="fixed inset-0 bg-slate-900/55 flex items-center justify-center z-[1100]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirmDialogTitle"
        @click.self="handleCancel"
        @keydown="handleEscape"
        tabindex="-1"
      >
        <div class="bg-surface rounded-lg w-[min(420px,92vw)] p-4 shadow-xl text-left" role="document">
          <h3 id="confirmDialogTitle" class="m-0 mb-2 text-xl font-semibold text-text-main">
            {{ state.title }}
          </h3>
          <p id="confirmDialogMessage" class="m-0 mb-4 text-text-muted leading-relaxed">
            {{ state.message }}
          </p>
          <div class="flex justify-end gap-3">
            <button
              v-if="state.showCancel"
              class="btn btn-secondary"
              @click="handleCancel"
            >
              {{ state.cancelText }}
            </button>
            <button
              :class="state.destructive ? 'btn btn-danger' : 'btn btn-primary'"
              @click="handleConfirm"
              ref="confirmButtonRef"
            >
              {{ state.confirmText }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useConfirm } from '@/composables/useConfirm'

const { state, confirm, handleEscape: handleEscapeKey } = useConfirm()
const confirmButtonRef = ref<HTMLButtonElement | null>(null)

function handleConfirm() {
  confirm(true)
}

function handleCancel() {
  confirm(false)
}

function handleEscape(event: KeyboardEvent) {
  handleEscapeKey(event)
}

// Focus confirm button when dialog opens
watch(() => state.value.show, async (show) => {
  if (show) {
    await nextTick()
    if (confirmButtonRef.value) {
      confirmButtonRef.value.focus()
    }
  }
})

// Handle keyboard events
onMounted(() => {
  document.addEventListener('keydown', handleEscape)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscape)
})
</script>

<style scoped>
/* Keep only transitions - all static styles moved to Tailwind */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-active .bg-surface,
.fade-leave-active .bg-surface {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.fade-enter-from .bg-surface,
.fade-leave-to .bg-surface {
  transform: scale(0.95);
  opacity: 0;
}
</style>

